import { FontWeight } from '@pasteltech/theme-provider'
import { Link } from 'gatsby'
import { memo } from 'react'
import styled from 'styled-components'
import { PagePaths } from '../../constants'
import { ArrowForwardIcon } from '../../svg-components'
import { DeepNullable, ScreenQuery } from '../../utilities'
import { StrapiImage, StrapiImageData } from '../strapi-image'

type ServicePreview = null | DeepNullable<{
  title: string
  description: string
  image: StrapiImageData
}>
type Props = {
  data: ServicePreview[] | null
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${ScreenQuery.mobile} {
    flex-direction: column;
  }
`
const Card = styled.div`
  flex: 1 0 50%;
  position: relative;
`
const ServiceImage = styled(StrapiImage)`
  width: 100%;
  height: 100%;
`

const ContentOverlay = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  bottom: ${({ theme }) => theme.sizes.spacings.xxxl}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${ScreenQuery.desktop} {
    bottom: ${({ theme }) => theme.sizes.spacings.xxxl}px;
    gap: ${({ theme }) => theme.sizes.spacings.lg}px;
  }
  ${ScreenQuery.tablet} {
    bottom: ${({ theme }) => theme.sizes.spacings.xl}px;
    gap: ${({ theme }) => theme.sizes.spacings.xxs}px;
  }
  ${ScreenQuery.mobile} {
    bottom: ${({ theme }) => theme.sizes.spacings.md}px;
    gap: ${({ theme }) => theme.sizes.spacings.sm}px;
  }
`
const Title = styled.div`
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  text-align: center;
  font-weight: ${FontWeight.bold};

  ${ScreenQuery.desktop} {
    font-size: ${({ theme }) => theme.sizes.fontSize.heading3}px;
  }
  ${ScreenQuery.tablet} {
    font-size: ${({ theme }) => theme.sizes.fontSize.headerline}px;
  }
  ${ScreenQuery.mobile} {
    font-size: ${({ theme }) => theme.sizes.fontSize.title}px;
  }
`
const Description = styled.div`
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  text-align: center;

  font-weight: ${FontWeight.medium};
  ${ScreenQuery.desktop} {
    font-size: ${({ theme }) => theme.sizes.fontSize.subtitle}px;
  }
  ${ScreenQuery.tablet} {
    font-size: ${({ theme }) => theme.sizes.fontSize.body}px;
  }
  ${ScreenQuery.mobile} {
    font-size: ${({ theme }) => theme.sizes.fontSize.label}px;
  }
`
const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary.containerContrast};
  border-radius: 50%;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:active {
    opacity: 0.6;
  }
  ${ScreenQuery.desktop} {
    width: 48px;
    height: 48px;
    padding: ${({ theme }) => theme.sizes.spacings.md}px;
  }
  ${ScreenQuery.tablet} {
    width: 40px;
    height: 40px;
  }
  ${ScreenQuery.mobile} {
    width: 35px;
    height: 35px;
  }
`
const StyledArrowForwardIcon = styled(ArrowForwardIcon)`
  path {
    stroke: ${({ theme }) => theme.colors.primary.containerContrast};
    fill: ${({ theme }) => theme.colors.primary.containerContrast};
  }
`

export const ServicePreviewList = memo(({ data }: Props) => {
  return (
    <Container>
      {(data ?? []).map((service) => (
        <Card key={service?.title}>
          <ServiceImage strapiImage={service?.image} isRequired />
          <ContentOverlay>
            <Description>{service?.description}</Description>
            <Title>{service?.title}</Title>
            <Link to={PagePaths.services}>
              <NavigationButton>
                <StyledArrowForwardIcon />
              </NavigationButton>
            </Link>
          </ContentOverlay>
        </Card>
      ))}
    </Container>
  )
})

ServicePreviewList.displayName = 'ServicePreviewList'
